import { useState, useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../../../../API';
import { normalizeStainBlock } from '../../../../../utils/metadataNormalization';
import { uploadFileInChunks } from '../../../../../utils/girderChunkedUpload';

/**
 * Build pluginMetadata.bdsa from a file or fileEntry (single source of truth for backend contract).
 * Entry may have: stain, block, regions, level, scan, magnification.
 */
function buildBdsaPluginMetadata(entry) {
    const stain = normalizeStainBlock(entry?.stain, 'stain');
    const regions = Array.isArray(entry?.regions) && entry.regions.length > 0
        ? entry.regions
        : (Array.isArray(entry?.block?.regionDefinitions) && entry.block.regionDefinitions?.length > 0
            ? entry.block.regionDefinitions
            : []);
    const blockId = entry?.block?._id ?? null;
    const level = Math.max(1, parseInt(entry?.level, 10) || 1);
    const scan = Math.max(1, parseInt(entry?.scan, 10) || 1);
    let magnification = entry?.magnification ?? '00x';
    if (magnification === 'unknown_mag') {
        magnification = '00x';
    }
    return {
        stain,
        regions,
        blockId: blockId || undefined,
        level,
        scan,
        magnification
    };
}

/**
 * True when we should send pluginMetadata (any bdsa field set beyond defaults).
 */
function hasBdsaMetadata(bdsa) {
    return (bdsa.stain !== null) ||
        (bdsa.regions.length > 0) ||
        (bdsa.blockId != null && bdsa.blockId !== '') ||
        bdsa.level !== 1 ||
        bdsa.scan !== 1 ||
        bdsa.magnification !== '00x';
}

/**
 * Hook for managing file upload state and operations
 */
export function useFileUpload({ doc, setDoc, networkId }) {
    const queryClient = useQueryClient();
    const [files, setFiles] = useState({});
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState({});
    const [duplicateFiles, setDuplicateFiles] = useState(null);
    const [acceptStagedLoading, setAcceptStagedLoading] = useState(false);
    
    /**
     * Upload method selection:
     * 
     * - 'item' (recommended): Create item first via POST /network/item with metadata,
     *   then upload file to that item. Metadata is passed via pluginMetadata in the
     *   event system, avoiding timing issues and multiple network requests.
     * 
     * - 'reference': Upload file to folder with reference parameter containing metadata.
     *   The data.process event handler (_onDataProcess) extracts metadata from reference.
     *   This is a fallback method but has timing dependencies.
     * 
     * - 'old': Upload file to folder, then set metadata via PUT /bdsa/item after upload.
     *   This requires multiple network requests and can have timing issues.
     * 
     * Note: The 'item' method is recommended as it ensures metadata exists at the time
     * of BdsaItem creation and avoids race conditions.
     */
    const UPLOAD_METHOD = 'item'; // 'item', 'reference', or 'old'

    const checkDuplicatesMutation = useMutation({
        mutationFn: ({caseFolderId, fileNames}) => api.post('/network/items/check-duplicates', {
            query: { caseFolderId },
            body: { fileNames }
        }),
    });

    const uploadFilesMutation = useMutation({
        mutationFn: ({caseId, filelist}) => api.post(`/bdsa/case/${caseId}/files`, {
            body: {files: filelist}
        }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['bdsa-case', networkId] });
        },
    });


    // Remove file function
    const removeFile = useCallback((name) => {
        setFiles(prevFiles => {
            const updatedFiles = {...prevFiles};
            delete updatedFiles[name];
            return updatedFiles;
        });
        setUploadProgress(prevProgress => {
            const updatedProgress = {...prevProgress};
            delete updatedProgress[name];
            return updatedProgress;
        });
    }, []);

    // Add staged items function (legacy: adds to pending list; prefer acceptAndSaveStagedItems for immediate save)
    const addStagedItems = useCallback((payload) => {
        setFiles(prevFiles => {
            const updated = {...prevFiles};
            
            // Handle payload structure: { items: [...], folders: [...] }
            const items = payload.items || (Array.isArray(payload) ? payload : []);
            const folders = payload.folders || [];
            
            // Add individually selected items
            items.forEach(item => {
                const key = item.name || item._id;
                // Check if item already exists (avoid duplicates)
                if (!updated[key]) {
                    updated[key] = {
                        name: item.name || 'Unknown',
                        size: item.size || 0,
                        type: item.meta?.mimeType || '',
                        block: null,
                        stain: null,
                        itemId: item._id,
                        isStaged: true,
                        sourceSelectionType: 'item',
                        isDuplicate: false
                    };
                }
            });
            
            // Add items from selected folders
            folders.forEach(folderSelection => {
                const { folderId, items: folderItems } = folderSelection;
                
                folderItems.forEach(item => {
                    const key = item.name || item._id;
                    // Check if item already exists (avoid duplicates)
                    if (!updated[key]) {
                        updated[key] = {
                            name: item.name || 'Unknown',
                            size: item.size || 0,
                            type: item.meta?.mimeType || '',
                            block: null,
                            stain: null,
                            itemId: item._id,
                            isStaged: true,
                            sourceStagingFolderId: folderId,
                            sourceSelectionType: 'folder',
                            isDuplicate: false
                        };
                    }
                });
            });
            
            return updated;
        });
    }, []);

    /**
     * Accept staged items with immediate save: single bulk move to case folder, then refetch case.
     * Does not add items to pending files list. On success, case doc is updated via refetch.
     */
    const acceptAndSaveStagedItems = useCallback(async (payload) => {
        const caseFolderId = doc?.folderId;
        if (!caseFolderId) {
            throw new Error('Case folder not available.');
        }
        const items = payload.items || (Array.isArray(payload) ? payload : []);
        const folders = payload.folders || [];
        const itemIds = [...items.map(item => item._id)];
        folders.forEach(({ items: folderItems }) => {
            (folderItems || []).forEach(item => itemIds.push(item._id));
        });
        if (itemIds.length === 0) {
            return null;
        }
        setAcceptStagedLoading(true);
        try {
            const moveResponse = await api.post('/network/item/move', {
                query: {
                    bulk: true,
                    folderId: caseFolderId
                },
                body: {
                    itemId: itemIds,
                    pluginMetadata: {
                        bdsa: { stain: null, regions: [] }
                    }
                }
            });
            if (moveResponse.status !== 200) {
                throw new Error(moveResponse.statusText || 'Move failed');
            }
            const caseRes = await api.get('bdsa/case', { query: { networkIdentifier: networkId } });
            if (caseRes.status === 200 && caseRes.data) {
                setDoc(caseRes.data);
            }
            queryClient.invalidateQueries({ queryKey: ['bdsa-case', networkId] });
            return caseRes.status === 200 ? caseRes.data : null;
        } catch (err) {
            console.error('acceptAndSaveStagedItems failed:', err);
            const message = err.response?.data?.message || err.message || 'Failed to add items to case';
            const failedId = err.response?.data?.itemId ?? null;
            if (failedId) {
                alert(`${message} (item: ${failedId})`);
            } else {
                alert(message);
            }
            throw err;
        } finally {
            setAcceptStagedLoading(false);
        }
    }, [doc, networkId, queryClient, setDoc]);

    // Upload file to Girder
    const uploadFileToGirder = useCallback(async (file, folderId, onProgress) => {
        onProgress({ status: 'uploading', progress: 0 });
        
        const uploadParams = {
            parentType: 'folder',
            parentId: folderId,
            name: file.name,
            size: file.size.toString(),
            mimeType: file.type || 'application/octet-stream'
        };
        
        const startUrl = api.url('/file', {query: uploadParams});
        const headers = {};
        if(localStorage.girderToken) {
            headers['Girder-Token'] = localStorage.girderToken;
        }
        
        onProgress({ status: 'uploading', progress: 10 });
        
        const startResponse = await fetch(startUrl, {
            method: 'POST',
            headers: headers
        });
        
        if(!startResponse.ok) {
            throw new Error(`Failed to start upload: ${startResponse.statusText}`);
        }
        
        const upload = await startResponse.json();
        
        onProgress({ status: 'uploading', progress: 20 });
        
        const chunkResult = await uploadFileInChunks(file, upload._id, upload, onProgress);
        
        const finalItemId = chunkResult._id || chunkResult.itemId;
        if (!finalItemId) {
            console.error('Could not find item ID in chunk response:', {
                chunkResult,
                uploadStartResponse: upload,
                availableFields: Object.keys(chunkResult)
            });
            throw new Error('Upload completed but item ID not found in chunk response. Response structure: ' + JSON.stringify(chunkResult));
        }
        
        onProgress({ status: 'completed', progress: 100 });
        
        return {
            _id: finalItemId,
            itemId: finalItemId
        };
    }, []);

    // New method: Upload with reference parameter for data.process event
    const uploadFileToGirderWithReference = useCallback(async (file, folderId, metadata, onProgress) => {
        onProgress({ status: 'uploading', progress: 0 });
        
        const normalizedStain = normalizeStainBlock(metadata?.stain);
        const normalizedBlock = normalizeStainBlock(metadata?.block);
        
        // Prepare reference parameter
        const referenceData = {
            bdsa: {
                stain: normalizedStain,
                block: normalizedBlock,
                level: 1,
                scan: 1
            }
        };
        const referenceJson = JSON.stringify(referenceData);
        
        const uploadParams = {
            parentType: 'folder',
            parentId: folderId,
            name: file.name,
            size: file.size.toString(),
            mimeType: file.type || 'application/octet-stream',
            reference: referenceJson
        };
        
        const startUrl = api.url('/file', {query: uploadParams});
        const headers = {};
        if(localStorage.girderToken) {
            headers['Girder-Token'] = localStorage.girderToken;
        }
        
        onProgress({ status: 'uploading', progress: 10 });
        
        const startResponse = await fetch(startUrl, {
            method: 'POST',
            headers: headers
        });
        
        if(!startResponse.ok) {
            console.error('uploadFileToGirderWithReference: Failed to start upload:', startResponse.statusText);
            throw new Error(`Failed to start upload: ${startResponse.statusText}`);
        }
        
        const upload = await startResponse.json();
        
        const itemId = upload.itemId;
        if (!itemId) {
            console.error('uploadFileToGirderWithReference: No itemId in upload response');
            throw new Error('No item ID in upload start response');
        }
        
        onProgress({ status: 'uploading', progress: 20 });
        
        const chunkResult = await uploadFileInChunks(file, upload._id, upload, onProgress);
        
        const chunkItemId = chunkResult._id || chunkResult.itemId;
        if (chunkItemId && chunkItemId !== itemId) {
            console.warn('uploadFileToGirderWithReference: Item ID mismatch!', {
                uploadStart: itemId,
                chunkResponse: chunkItemId
            });
        }
        
        onProgress({ status: 'completed', progress: 100 });
        
        return {
            _id: chunkItemId || itemId,
            itemId: chunkItemId || itemId
        };
    }, []);

    // New method: Create item first, then upload file to it
    const uploadFileToGirderViaItem = useCallback(async (file, folderId, metadata, onProgress) => {
        onProgress({ status: 'creating_item', progress: 0 });

        const bdsa = buildBdsaPluginMetadata(metadata);
        const sendMetadata = hasBdsaMetadata(bdsa);

        // Step 1: Create the item first (with metadata if any bdsa field set)
        let createdItem;
        let itemId;

        if (sendMetadata) {
            const createItemResponse = await api.post('/network/item', {
                query: {
                    name: file.name,
                    folderId: folderId,
                    description: ''
                },
                body: {
                    pluginMetadata: { bdsa }
                }
            });
            createdItem = createItemResponse.data;
            itemId = createdItem._id;
        } else {
            const createItemResponse = await api.post('/network/item', {
                query: {
                    name: file.name,
                    folderId: folderId,
                    description: ''
                }
            });
            createdItem = createItemResponse.data;
            itemId = createdItem._id;
        }
        
        if(!itemId) {
            throw new Error('No item ID returned from item creation');
        }
        
        onProgress({ status: 'uploading', progress: 10 });
        
        // Step 2: Upload file to the item (parentType=item)
        const uploadParams = {
            parentType: 'item',  // Upload to item, not folder
            parentId: itemId,    // The item we just created
            name: file.name,
            size: file.size.toString(),
            mimeType: file.type || 'application/octet-stream'
        };
        
        const startUrl = api.url('/file', {query: uploadParams});
        const headers = {};
        if(localStorage.girderToken) {
            headers['Girder-Token'] = localStorage.girderToken;
        }
        
        onProgress({ status: 'uploading', progress: 20 });
        
        const startResponse = await fetch(startUrl, {
            method: 'POST',
            headers: headers
        });
        
        if(!startResponse.ok) {
            console.error('uploadFileToGirderViaItem: Failed to start upload:', startResponse.statusText);
            throw new Error(`Failed to start upload: ${startResponse.statusText}`);
        }
        
        const upload = await startResponse.json();
        
        const chunkResult = await uploadFileInChunks(file, upload._id, upload, onProgress);
        
        const finalItemId = chunkResult._id || chunkResult.itemId || itemId;
        
        onProgress({ status: 'completed', progress: 100 });
        
        return {
            _id: finalItemId,
            itemId: finalItemId
        };
    }, []);

    // File upload handler
    const updateFiles = useCallback(async (event) => {
        const newFiles = [...event.target.files];
        event.target.value = '';
        
        const updatedFiles = {...files};
        const updatedProgress = {...uploadProgress};
        
        const caseFolderId = doc?.folderId || null;
        const fileNamesToCheck = newFiles
            .filter(f => !updatedFiles[f.name])
            .map(f => f.name);
        
        let duplicates = {};
        if(fileNamesToCheck.length > 0 && caseFolderId) {
            try {
                const duplicateCheck = await checkDuplicatesMutation.mutateAsync({
                    caseFolderId: caseFolderId,
                    fileNames: fileNamesToCheck
                });
                if(duplicateCheck.status === 200 && duplicateCheck.data) {
                    duplicates = duplicateCheck.data;
                }
            } catch(err) {
                console.error('Failed to check for duplicates:', err);
            }
        }
        
        for(const file of newFiles) {
            if(!updatedFiles[file.name]) {
                const isDuplicate = duplicates[file.name]?.exists === true;
                updatedFiles[file.name] = {
                    name: file.name || 'Unknown',
                    size: file.size || 0,
                    type: file.type || '',
                    block: null,
                    stain: null,
                    file: file,
                    isDuplicate: isDuplicate,
                    duplicateLocation: isDuplicate ? duplicates[file.name].location : null
                };
                updatedProgress[file.name] = isDuplicate 
                    ? { status: 'duplicate', progress: 0 }
                    : { status: 'not_started', progress: 0 };
            }
        }
        
        setFiles(updatedFiles);
        setUploadProgress(updatedProgress);
    }, [files, uploadProgress, doc, checkDuplicatesMutation]);

    // Save files
    const saveFiles = useCallback(async (event) => {
        event.target.disabled = true;
        setUploading(true);
        
        const caseFolderId = doc?.folderId || null;
        if(!caseFolderId) {
            alert('Case folder not available. Please ensure the case has been saved first.');
            event.target.disabled = false;
            setUploading(false);
            return;
        }
        
        try {
            const updatedFiles = {...files};
            const updatedProgress = {...uploadProgress};
            const filelist = [];
            const stagingFolderIdsToTryDelete = new Set(); // Track folders to delete after successful moves
            
            for(const fileName in updatedFiles) {
                const fileEntry = updatedFiles[fileName];
                
                if(fileEntry.isDuplicate) {
                    continue;
                }
                
                // Handle staged items (move existing items)
                if(fileEntry.isStaged && fileEntry.itemId) {
                    try {
                        updatedProgress[fileName] = { status: 'moving', progress: 0 };
                        setUploadProgress({...updatedProgress});

                        const bdsa = buildBdsaPluginMetadata(fileEntry);

                        const moveResponse = await api.post('/network/item/move', {
                            query: {
                                itemId: fileEntry.itemId,
                                folderId: caseFolderId
                            },
                            body: {
                                pluginMetadata: { bdsa }
                            }
                        });
                        
                        if(moveResponse.status !== 200) {
                            throw new Error(`Failed to move item: ${moveResponse.statusText}`);
                        }
                        
                        updatedProgress[fileName] = { status: 'completed', progress: 100 };
                        setUploadProgress({...updatedProgress});
                        
                        // Track staging folder for deletion if this item came from a folder selection
                        if (fileEntry.sourceStagingFolderId) {
                            stagingFolderIdsToTryDelete.add(fileEntry.sourceStagingFolderId);
                        }
                        
                        filelist.push({
                            name: fileEntry.name,
                            block: fileEntry.block,
                            stain: fileEntry.stain,
                            itemId: fileEntry.itemId
                        });
                    } catch(err) {
                        console.error(`Failed to move staged item ${fileName}:`, err);
                        updatedProgress[fileName] = { 
                            status: 'error', 
                            progress: 0, 
                            error: err.message 
                        };
                        setUploadProgress({...updatedProgress});
                    }
                    continue;
                }
                
                // Handle regular file uploads
                const fileObj = fileEntry.file || fileEntry;
                
                try {
                    const updateProgress = (progress) => {
                        updatedProgress[fileName] = progress;
                        setUploadProgress({...updatedProgress});
                    };
                    
                    const normalizedStain = normalizeStainBlock(fileEntry.stain);
                    const normalizedBlock = normalizeStainBlock(fileEntry.block);
                    
                    let uploadedItem;
                    let itemId;
                    
                    // Toggle between upload methods
                    if (UPLOAD_METHOD === 'item') {
                        uploadedItem = await uploadFileToGirderViaItem(
                            fileObj,
                            caseFolderId,
                            {
                                stain: fileEntry.stain,
                                block: fileEntry.block,
                                regions: fileEntry.regions,
                                level: fileEntry.level ?? 1,
                                scan: fileEntry.scan ?? 1,
                                magnification: fileEntry.magnification ?? '00x'
                            },
                            updateProgress
                        );
                        itemId = uploadedItem._id || uploadedItem.itemId;
                        // Metadata is set via network.item.saved.local event when item is created
                    } else if (UPLOAD_METHOD === 'reference' && (normalizedStain !== null || normalizedBlock !== null)) {
                        uploadedItem = await uploadFileToGirderWithReference(
                            fileObj, 
                            caseFolderId, 
                            { stain: fileEntry.stain, block: fileEntry.block },
                            updateProgress
                        );
                        itemId = uploadedItem._id || uploadedItem.itemId;
                        // Don't call PUT /bdsa/item - metadata will be set via data.process event
                    } else {
                        uploadedItem = await uploadFileToGirder(fileObj, caseFolderId, updateProgress);
                        itemId = uploadedItem._id || uploadedItem.itemId;
                        
                        if(!itemId) {
                            throw new Error('No item ID returned from upload');
                        }
                        
                        // Old method: set metadata after upload (block is UI-only, not sent)
                        const regions = Array.isArray(fileEntry.regions) ? fileEntry.regions : [];
                        if (normalizedStain !== null || regions.length > 0) {
                            try {
                                await api.put(`/bdsa/item/${itemId}`, {
                                    body: {
                                        body: {
                                            stain: normalizedStain,
                                            regions: regions.length > 0 ? regions : []
                                        }
                                    }
                                });
                            } catch (metaErr) {
                                console.error(`Failed to set metadata on item ${itemId}:`, metaErr);
                                // Log error but don't fail the upload - metadata can be set later via UI
                            }
                        }
                    }
                    
                    if(!itemId) {
                        throw new Error('No item ID returned from upload');
                    }
                    
                    updatedFiles[fileName].itemId = itemId;
                    
                    filelist.push({
                        name: fileEntry.name,
                        block: fileEntry.block,
                        stain: fileEntry.stain,
                        itemId: itemId
                    });
                    
                } catch(err) {
                    console.error(`Failed to upload ${fileName}:`, err);
                    updatedProgress[fileName] = { 
                        status: 'error', 
                        progress: 0, 
                        error: err.message 
                    };
                    setUploadProgress({...updatedProgress});
                }
            }
            
            if(filelist.length > 0) {
                const resp = await uploadFilesMutation.mutateAsync({caseId: doc._id, filelist: filelist});
                if(resp.status === 200){
                    setFiles({});
                    setUploadProgress({});
                    setDoc(resp.data);
                    
                    // Attempt to delete staging folders that were emptied
                    // This happens after all items have been successfully moved
                    for (const folderId of stagingFolderIdsToTryDelete) {
                        try {
                            const deleteResponse = await api.delete(`/network/staging/folder/${folderId}`);
                            if (deleteResponse.status === 200 && deleteResponse.data?.deleted) {
                                // Staging folder successfully deleted
                            } else {
                                console.warn(`Staging folder ${folderId} could not be deleted: ${deleteResponse.data?.reason || 'unknown reason'}`);
                            }
                        } catch (deleteErr) {
                            // Non-fatal: log warning but don't fail the save operation
                            console.warn(`Failed to delete staging folder ${folderId} after save:`, deleteErr);
                        }
                    }
                }
            }
        } catch(err) {
            console.error('Failed to save files:', err);
        } finally {
            setUploading(false);
            event.target.disabled = false;
        }
    }, [files, uploadProgress, doc, uploadFileToGirder, uploadFilesMutation, setDoc]);

    /**
     * Upload local files immediately into the case (quarantined, empty stain/regions).
     * Used by Enhanced Add slides → This computer.
     */
    const uploadLocalFiles = useCallback(async (fileList) => {
        const caseFolderId = doc?.folderId;
        if (!caseFolderId) {
            throw new Error('Case folder not available.');
        }
        const localFiles = Array.from(fileList || []).filter(Boolean);
        if (localFiles.length === 0) return null;

        setUploading(true);
        const filelist = [];
        try {
            for (const file of localFiles) {
                const uploaded = await uploadFileToGirderViaItem(
                    file,
                    caseFolderId,
                    {
                        stain: null,
                        regions: [],
                        level: 1,
                        scan: 1,
                        magnification: '00x',
                    },
                    (progress) => {
                        setUploadProgress((prev) => ({ ...prev, [file.name]: progress }));
                    },
                );
                filelist.push({
                    name: file.name,
                    block: null,
                    stain: null,
                    itemId: uploaded.itemId || uploaded._id,
                });
            }
            if (filelist.length > 0 && doc?._id) {
                const resp = await uploadFilesMutation.mutateAsync({ caseId: doc._id, filelist });
                if (resp.status === 200 && resp.data) {
                    setDoc(resp.data);
                } else {
                    const caseRes = await api.get('bdsa/case', { query: { networkIdentifier: networkId } });
                    if (caseRes.status === 200 && caseRes.data) {
                        setDoc(caseRes.data);
                    }
                }
            } else if (filelist.length > 0) {
                const caseRes = await api.get('bdsa/case', { query: { networkIdentifier: networkId } });
                if (caseRes.status === 200 && caseRes.data) {
                    setDoc(caseRes.data);
                }
            }
            queryClient.invalidateQueries({ queryKey: ['bdsa-case', networkId] });
            const caseRes = await api.get('bdsa/case', { query: { networkIdentifier: networkId } });
            if (caseRes.status === 200 && caseRes.data) {
                setDoc(caseRes.data);
                return caseRes.data;
            }
            return null;
        } catch (err) {
            console.error('uploadLocalFiles failed:', err);
            alert(err?.message || 'Failed to upload files');
            throw err;
        } finally {
            setUploading(false);
            setUploadProgress({});
        }
    }, [doc, networkId, queryClient, setDoc, uploadFileToGirderViaItem, uploadFilesMutation]);

    return {
        files,
        setFiles,
        uploading,
        uploadProgress,
        duplicateFiles,
        setDuplicateFiles,
        updateFiles,
        saveFiles,
        removeFile,
        addStagedItems,
        acceptAndSaveStagedItems,
        acceptStagedLoading,
        uploadLocalFiles,
    };
}
